import type { TFunction } from 'react-i18next';
import type { NavigateFunction } from 'react-router-dom';
import { ReactComponent as ConfluenceIcon } from 'src/assets/tailwind/confluence.svg';
import { ReactComponent as FolderIcon } from 'src/assets/tailwind/folder.svg';
import { ReactComponent as GoogleDriveIcon } from 'src/assets/tailwind/google_drive.svg';
import { ReactComponent as ElogoIcon } from 'src/assets/tailwind/Logo.svg';
import { ReactComponent as OneDriveIcon } from 'src/assets/tailwind/one-drive.svg';
import { ReactComponent as SharePointIcon } from 'src/assets/tailwind/share-point.svg';
import type { DropdownOption } from 'src/components/ui/dropdown';
import { ONBOARDING_STEPS_TARGET } from 'src/components/UserOnboard/constants';
import { IS_MINI_EXPERTS_DISABLED } from 'src/config';
import type { ChatFile } from 'src/content/Chat/types';
import {
  buildAssistantChatPath,
  buildChatPath,
  NEW_CHAT_PATH
} from 'src/content/ChatV2/constants';
import { SETTINGS_TABS } from 'src/content/Settings/utils';
import type { SidebarIcon } from 'src/layouts/SidebarV2/icons';
import { sidebarIcons } from 'src/layouts/SidebarV2/icons';
import type {
  ChatHistoryItem,
  FavouriteModel,
  NavAvatar,
  NavItemModel
} from 'src/layouts/SidebarV2/types';
import type { UserFavourites } from 'src/redux/slices/data';
import type { StringKeys } from 'src/types/base';
import {
  CHAT_CLASS,
  FAVOURITE_TYPE,
  RESOURCE_PROVIDERS,
  RESOURCE_TYPE,
  USER_APP_PERMISSIONS
} from 'src/types/enum';
import {
  ADMIN_CONSOLE_ROUTES,
  ANALYTICS_ROUTES,
  DATE_FORMAT,
  NAVIGATIONS,
  USER_ROUTES
} from 'src/utils/constants';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { formatDate } from 'src/utils/utils';
import { COLOR_SCHEME_MAP } from 'src/utils/utilsV2/utils';

export const favouriteToChatFileMapper = (
  favourite: UserFavourites
): ChatFile => {
  return {
    author: null,
    file_id: favourite.item_id,
    name: favourite.display_name || favourite.name,
    type_id: favourite.type_id,
    type_name: '',
    tag_name: 'null',
    tag_id: 0,
    format: '',
    original_format: '',
    created_on: formatDate(favourite.created_on, DATE_FORMAT),
    provider_id: favourite?.provider_id || null,
    parent_id: favourite?.parent_id || null,
    type_code: favourite.code,
    id: favourite.item_id?.toString()
  };
};

const PROVIDER_ICON_MAP: Partial<Record<RESOURCE_PROVIDERS, SidebarIcon>> = {
  [RESOURCE_PROVIDERS.ONEDRIVE]: OneDriveIcon,
  [RESOURCE_PROVIDERS.SHAREPOINT]: SharePointIcon,
  [RESOURCE_PROVIDERS.CONFLUENCE]: ConfluenceIcon,
  [RESOURCE_PROVIDERS.GOOGLEDRIVE]: GoogleDriveIcon
};

const FOLDER_LIKE_CODES = new Set<string>([
  RESOURCE_TYPE.COLLECTION,
  RESOURCE_TYPE.FOUND_COLLECTION,
  RESOURCE_TYPE.FOLDER
]);

export const resolveWorkspaceLeading = (
  item: StringKeys
): Pick<FavouriteModel, 'icon' | 'avatar'> => {
  if (FOLDER_LIKE_CODES.has(item.code)) {
    return {
      icon:
        PROVIDER_ICON_MAP[item.provider_type as RESOURCE_PROVIDERS] ??
        FolderIcon
    };
  }
  return {
    avatar: {
      text: item.initials,
      size: 'xs',
      colorScheme: COLOR_SCHEME_MAP[item?.initials_color] ?? 'default',
      variant: 'round'
    }
  };
};

export const resolveAssistantAvatar = (
  item: StringKeys,
  iconMap: Record<number, string>
): NavAvatar => {
  const iconUrl = iconMap[Number(item.settings?.icon_id)];
  return iconUrl
    ? { src: iconUrl, variant: 'round', size: 'xs' }
    : { src: ElogoIcon, variant: 'round', colorScheme: 'default', size: 'xs' };
};

export const resolveProjectAvatar = (item: StringKeys): NavAvatar => ({
  src: ElogoIcon,
  variant: 'round',
  colorScheme: item.settings?.icon ?? 'default',
  size: 'xs'
});

export const resolveFavouriteLink = (
  item: StringKeys,
  kind: FAVOURITE_TYPE
): string => {
  if (kind === FAVOURITE_TYPE.PROJECT) {
    return `/projects/${item.item_id}`;
  }
  return `/data-library/${item.item_id}`;
};

/** Is chat `id` the one open at `pathname` (any `/chat/:id`-shaped route)? Pass `useCurrentPathname()`. */
export const isChatOpenAt = (pathname: string, id: number): boolean => {
  const segments = pathname.split('/').filter(Boolean);
  return segments.includes('chat') && segments[segments.length - 1] === String(id);
};

/** The href a history row reopens its chat on: assistant surface, else conversation. */
export const resolveChatHistoryLink = (chat: ChatHistoryItem): string => {
  if (chat.chatClass === CHAT_CLASS.ASSISTANT && chat.rid) {
    return buildAssistantChatPath(chat.rid, chat.id);
  }
  return buildChatPath(chat.id);
};

export const favouriteToModel = (
  item: StringKeys,
  kind: FAVOURITE_TYPE,
  iconMap: Record<number, string>
): FavouriteModel => {
  const base: FavouriteModel = {
    id: `${kind}-${item.item_id}`,
    name: item.display_name || item.name,
    kind,
    isFavourite: true,
    to:
      kind === FAVOURITE_TYPE.ASSISTANT
        ? undefined
        : resolveFavouriteLink(item, kind),
    canStartChat: true
  };

  if (kind === FAVOURITE_TYPE.ASSISTANT) {
    return {
      ...base,
      avatar: resolveAssistantAvatar(item, iconMap),
      hasFilePermission: !!item?.has_permission
    };
  }
  if (kind === FAVOURITE_TYPE.PROJECT) {
    return { ...base, avatar: resolveProjectAvatar(item), canStartChat: true };
  }
  return {
    ...base,
    ...resolveWorkspaceLeading(item),
    hasFilePermission: !!item.access_control?.['query-files-folders']?.value
  };
};

// Permission-gated navigation routes for the profile menu — mirrors the V1
// getPopupRoutes logic, mapped to V2 (currentColor) icons.
export const buildProfileRoutes = (
  navigate: NavigateFunction,
  userAppPermissions: StringKeys,
  isNonTrialUser: boolean | undefined,
  isSysAdminUser: boolean
): DropdownOption[] => {
  if (isSysAdminUser) {
    return [
      {
        id: 'admin-console',
        label: T.adminConsole,
        icon: sidebarIcons.settings,
        onClick: () => navigate('/admin-console')
      }
    ];
  }

  const routes: DropdownOption[] = [];

  const canViewUsers =
    userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_USERS]?.value;
  const canViewRoles =
    userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_ROLES]?.value;
  const canViewGroups =
    userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_USER_GROUPS]?.value ||
    userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_OWN_USER_GROUPS]?.value;

  if (canViewUsers) {
    routes.push({
      id: ONBOARDING_STEPS_TARGET.USERS_NAVIGATION_LINK,
      label: T.userAndRoles,
      icon: sidebarIcons.users,
      onClick: () => navigate(`/${USER_ROUTES.USERS}`)
    });
  } else if (isNonTrialUser && canViewRoles) {
    routes.push({
      id: 'roles',
      label: T.userAndRoles,
      icon: sidebarIcons.users,
      onClick: () => navigate(`/${USER_ROUTES.ROLES}`)
    });
  } else if (canViewGroups) {
    routes.push({
      id: 'groups',
      label: T.userAndRoles,
      icon: sidebarIcons.users,
      onClick: () => navigate(`/${USER_ROUTES.USER_GROUPS}`)
    });
  }

  const canViewAnalytics =
    userAppPermissions?.[USER_APP_PERMISSIONS.VIEW_ANALYTICS]?.value;

  if (canViewAnalytics) {
    routes.push(
      {
        id: 'analytics',
        label: T.analytics,
        icon: sidebarIcons.analytics,
        onClick: () => navigate(ANALYTICS_ROUTES.INTERNAL_ANALYTICS)
      },
      {
        id: 'usage',
        label: T.usage,
        icon: sidebarIcons.usage,
        onClick: () => navigate(ANALYTICS_ROUTES.USAGE)
      },
      {
        id: 'settings',
        label: T.settings,
        icon: sidebarIcons.settings,
        onClick: () => navigate(`/settings/${SETTINGS_TABS.SYSTEM_SETTINGS}`)
      }
    );
  } else {
    routes.push({
      id: 'settings',
      label: T.settings,
      icon: sidebarIcons.settings,
      onClick: () => navigate(`/settings/${SETTINGS_TABS.PROFILE}`)
    });
  }

  return routes;
};

export const buildAdminConsoleItems = (
  t: TFunction<'translation', undefined>,
  navigate: NavigateFunction,
  pathname: string
): NavItemModel[] => {
  const isOrgNotifications = pathname.includes('organization-notifications');

  return [
    {
      id: 'tenants',
      name: t(T.tenants),
      icon: sidebarIcons.workspaces,
      onClick: () => navigate(ADMIN_CONSOLE_ROUTES.TENANTS),
      active: pathname.includes('tenants')
    },
    {
      id: 'models',
      name: t(T.models),
      icon: sidebarIcons.settings,
      onClick: () => navigate(ADMIN_CONSOLE_ROUTES.MODELS),
      active: pathname.includes('models')
    },
    {
      id: 'analytics',
      name: t(T.analytics),
      icon: sidebarIcons.apps,
      onClick: () => navigate(ADMIN_CONSOLE_ROUTES.USAGE),
      active: pathname.includes('usage') || pathname.includes('analytics')
    },
    {
      id: 'notifications',
      name: t(T.notifications),
      icon: sidebarIcons.bell,
      onClick: () => navigate(ADMIN_CONSOLE_ROUTES.NOTIFICATIONS),
      active: pathname.includes('notifications') && !isOrgNotifications
    },
    {
      id: 'org-notifications',
      name: t(T.organizationNotifications),
      icon: sidebarIcons.bell,
      onClick: () => navigate(ADMIN_CONSOLE_ROUTES.ORG_NOTIFICATIONS),
      active: isOrgNotifications
    }
  ];
};

export type SidebarFeatureAccess = {
  canUseChat: boolean;
  canUseAssistants: boolean;
  canUseWorkflows: boolean;
  canUseProjects: boolean;
  canUseApps: boolean;
  canUseAdvanceSearch: boolean;
  canUseConnectors: boolean;
};

export const buildSidebarMenuItems = (
  t: TFunction<'translation', undefined>,
  startNewChat: () => void,
  canViewWorkspaces: boolean,
  workspacesDot:
    | {
        endIcon: import('./types').NavItemModel['endIcon'];
        endIconColorClass: string;
      }
    | undefined,
  featureAccess: SidebarFeatureAccess
): NavItemModel[] => {
  const {
    canUseChat,
    canUseAssistants,
    canUseWorkflows,
    canUseProjects,
    canUseApps,
    canUseAdvanceSearch,
    canUseConnectors
  } = featureAccess;

  const items: NavItemModel[] = [];

  if (canUseChat) {
    items.push(
      {
        id: 'new-chat',
        name: t(T.createNewChat),
        icon: sidebarIcons.newChat,
        to: NEW_CHAT_PATH,
        onClick: startNewChat
      }
    );
  }

  if (canUseAssistants) {
    items.push({
      id: 'assistants',
      name: t(T.assistants),
      icon: sidebarIcons.assistants,
      to: NAVIGATIONS.ASSISTANTS
    });
  }

  if (canUseAdvanceSearch) {
    items.push({
      id: 'advanced-search',
      name: t(T.advancedSearch),
      icon: sidebarIcons.search,
      to: NAVIGATIONS.ADVANCE_SEARCH
    });
  }
  items.push({
    id: 'skills',
    name: t(T.skills),
    icon: sidebarIcons.skills,
    to: NAVIGATIONS.SKILLS,
    badge: { variant: 'Beta' }
  });

  if (!IS_MINI_EXPERTS_DISABLED && canUseApps) {
    items.push({
      id: 'apps',
      name: t(T.miniExperts),
      icon: sidebarIcons.apps,
      to: NAVIGATIONS.APPS
    });
  }

  if (canViewWorkspaces) {
    items.push({
      id: 'workspaces',
      name: t(T.workspaces),
      icon: sidebarIcons.workspaces,
      to: NAVIGATIONS.DATA_LIBRARY,
      ...(workspacesDot && {
        endIcon: workspacesDot.endIcon,
        endIconColorClass: workspacesDot.endIconColorClass
      })
    });
  }

  if (canUseConnectors) {
    items.push({
      id: 'connectors',
      name: t(T.connectors),
      icon: sidebarIcons.connectors,
      to: NAVIGATIONS.CONNECTORS
    });
  }

  if (canUseWorkflows) {
    items.push({
      id: 'workflows',
      name: t(T.workflowsNavLabel),
      icon: sidebarIcons.workflows,
      to: NAVIGATIONS.WORKFLOWS
    });
  }

  if (canUseProjects) {
    items.push({
      id: 'projects',
      name: t(T.projects),
      icon: sidebarIcons.projects,
      to: NAVIGATIONS.PROJECTS
    });
  }

  return items;
};
